// src/components/Dashboard/RecentTransactions.tsx
import React from 'react'
import {useNavigate} from 'react-router-dom'
import {Transaction} from '../../types'
import {formatUSD, formatDate, getRelativeTime, truncateId} from '../../utils/helpers'
import {ArrowLeft, ExternalLink} from 'lucide-react'

interface RecentTransactionsProps {
    transactions: Transaction[]
}

const RecentTransactions: React.FC<RecentTransactionsProps> = ({transactions}) => {
    const navigate = useNavigate()

    if (!transactions || transactions.length === 0) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                <p className="text-gray-500">لا توجد معاملات حديثة</p>
            </div>
        )
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">آخر المعاملات</h3>
                <button
                    onClick={() => navigate('/transactions')}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                    عرض الكل
                    <ArrowLeft className="w-4 h-4" />
                </button>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">المعرف</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">المستخدم</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">النوع</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">المبلغ</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الحالة</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">التاريخ</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الإجراءات</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {transactions.slice(0, 10).map((tx) => (
                            <tr key={tx.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    #{tx.id}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm text-gray-600" title={tx.user_id}>
                                        {truncateId(tx.user_id, 10)}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${tx.type === 'BUY'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-orange-100 text-orange-800'
                                        }`}>
                                        {tx.type === 'BUY' ? 'شراء' : 'بيع'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">{formatUSD(tx.amount_usd)}</p>
                                        <p className="text-xs text-gray-500">{tx.amount_lbp.toLocaleString()} LL</p>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${tx.status === 'completed'
                                        ? 'bg-green-100 text-green-800'
                                        : tx.status === 'pending'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : 'bg-red-100 text-red-800'
                                        }`}>
                                        {tx.status === 'completed' ? 'مكتملة' : tx.status === 'pending' ? 'قيد الانتظار' : 'فشلت'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" title={formatDate(tx.created_at)}>
                                    {getRelativeTime(tx.created_at)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <button
                                        onClick={() => navigate(`/transactions?id=${tx.id}`)}
                                        className="text-blue-600 hover:text-blue-800"
                                        title="عرض التفاصيل"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default RecentTransactions